// Aggregation for AdminAnalytics.tsx — pure (no React / fetch), unit-tested by
// adminAnalytics.test.ts.
//
// The page used to download up to 10,000 raw analytics_events rows and count them
// in the browser. It now receives one row per (UTC day, event type) from the
// admin_analytics_daily_events RPC (migration 20261016000200) and derives the same
// numbers from those: totals + trend vs the previous 30 days, the event breakdown,
// the headline tiles and the 14-day chart. Windows are whole UTC days (the RPC
// buckets by UTC day): "last 30 days" = today and the 29 days before it.

export interface DailyEventRow {
  day: string;          // YYYY-MM-DD (UTC)
  event_type: string;
  event_count: number;  // bigint arrives as a number from PostgREST; coerced defensively
}

export interface BreakdownRow {
  type: string;
  count: number;
  prevCount: number;
  /** % change vs the previous 30 days; null when there is no previous data */
  trendPct: number | null;
}

export interface ChartDay {
  date: string;
  total: number;
  /** event type with the most events that day ("" when none) — picks the bar colour */
  dominant: string;
}

export interface Dashboard {
  totalCurrent: number;
  totalPrev: number;
  totalTrendPct: number | null;
  breakdown: BreakdownRow[];
  /** current-window count per event type (for the headline tiles) */
  currentByType: Record<string, number>;
  chart: ChartDay[];
}

export const WINDOW_DAYS = 30;
export const CHART_DAYS = 14;

const MS_PER_DAY = 86_400_000;

/** YYYY-MM-DD in UTC. */
export function utcDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Day string n days from `day` (negative = earlier). */
export function addDays(day: string, n: number): string {
  return utcDay(new Date(Date.parse(`${day}T00:00:00Z`) + n * MS_PER_DAY));
}

/** Start of the earliest day the page needs (today − 59): current 30 + previous 30. */
export function windowStart(now: Date): Date {
  return new Date(Date.parse(`${addDays(utcDay(now), -(2 * WINDOW_DAYS - 1))}T00:00:00Z`));
}

/** Start of the current 30-day window (today − 29), for the top-products queries. */
export function currentWindowStart(now: Date): Date {
  return new Date(Date.parse(`${addDays(utcDay(now), -(WINDOW_DAYS - 1))}T00:00:00Z`));
}

export function trendPct(current: number, previous: number): number | null {
  return previous === 0 ? null : ((current - previous) / previous) * 100;
}

export function buildDashboard(rows: DailyEventRow[], now: Date): Dashboard {
  const today = utcDay(now);
  const currentFrom = addDays(today, -(WINDOW_DAYS - 1));
  const prevFrom = addDays(today, -(2 * WINDOW_DAYS - 1));
  const prevTo = addDays(today, -WINDOW_DAYS);

  const curr: Record<string, number> = {};
  const prev: Record<string, number> = {};
  const byDay: Record<string, Record<string, number>> = {};
  let totalCurrent = 0;
  let totalPrev = 0;

  for (const r of rows) {
    const n = Number(r.event_count);
    if (!Number.isFinite(n) || n <= 0 || typeof r.day !== "string") continue;
    const day = r.day.slice(0, 10);
    if (day >= currentFrom && day <= today) {
      curr[r.event_type] = (curr[r.event_type] ?? 0) + n;
      totalCurrent += n;
      const dayCounts = (byDay[day] ??= {});
      dayCounts[r.event_type] = (dayCounts[r.event_type] ?? 0) + n;
    } else if (day >= prevFrom && day <= prevTo) {
      prev[r.event_type] = (prev[r.event_type] ?? 0) + n;
      totalPrev += n;
    }
  }

  const breakdown: BreakdownRow[] = Object.entries(curr)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([type, count]) => ({
      type,
      count,
      prevCount: prev[type] ?? 0,
      trendPct: trendPct(count, prev[type] ?? 0),
    }));

  const chart: ChartDay[] = Array.from({ length: CHART_DAYS }, (_, i) => {
    const date = addDays(today, -(CHART_DAYS - 1 - i));
    const counts = byDay[date] ?? {};
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return {
      date,
      total: entries.reduce((s, [, n]) => s + n, 0),
      dominant: entries[0]?.[0] ?? "",
    };
  });

  return {
    totalCurrent,
    totalPrev,
    totalTrendPct: trendPct(totalCurrent, totalPrev),
    breakdown,
    currentByType: curr,
    chart,
  };
}
